// Shared via top-level window so both editor and canvas iframe bundles
// reference the same object (Parcel creates separate module instances,
// and iframe has its own window).
// Walk up to the topmost same-origin ancestor so cross-origin embed
// (landing → editor → canvas) still shares scope between editor chrome
// and canvas iframe.
// Walk to the topmost same-origin ancestor window. Exported so other
// modules (state.js, methods/index.js) don't redefine it.
export const resolveTopWindow = () => {
  if (typeof window === 'undefined') return globalThis
  let w = window
  while (w.parent && w.parent !== w) {
    try {
      void w.parent.document
      w = w.parent
    } catch {
      break
    }
  }
  return w
}
const topWindow = resolveTopWindow()

// Canvas mode constants — single source for both editor chrome and
// canvas iframe so we don't drift on string typos.
export const MODES = Object.freeze({
  DEFAULT: 'default',
  PAN: 'pan',
  INSPECT: 'inspect',
  INTERACT: 'interact'
})
export const HANDLE_NAMES = Object.freeze([
  'CvHandleNW',
  'CvHandleN',
  'CvHandleNE',
  'CvHandleE',
  'CvHandleSE',
  'CvHandleS',
  'CvHandleSW',
  'CvHandleW'
])

// ── Canvas layering contract (editor-chrome only — NOT shared with the
// per-tile iframe; each embedded project has its own isolated document and
// stacking) ──────────────────────────────────────────────────────────────
//
// There is no z-index scale anywhere in the repo's design system — every
// package hand-picks raw numbers. Two independent chrome layers in THIS
// package (CanvasEditor, the docked rail sidebars) had landed on 1300/1200 —
// numerically ABOVE the platform AppHeader's own zIndex:1100
// (shared/components/AppHeader.js, "above every canvas element" by
// contract) — so canvas's screen-space overlay (rulers, marquee, selection
// box, resize handles, item titles, tooltips, modes bar — everything
// CanvasOverlayV2 renders inside CanvasEditor's `position:fixed` stacking
// context) and the left/right rail sidebars painted OVER the header,
// org switcher, and any header dropdown (Nika, 2026-08-02).
//
// CanvasEditor is `position:fixed` + a real z-index, which makes it a
// stacking context: every descendant, at ANY of ITS OWN z-index values,
// paints as one unit at CanvasEditor's rank relative to the rest of the
// page — so this is the ONE ancestor value that decides whether canvas
// chrome can ever occlude the platform header, regardless of how small an
// individual overlay child's own z-index is.
//
// PLATFORM_HEADER mirrors (does not import — cross-package, and
// shared/** is read-only without explicit permission) AppHeader's zIndex.
// If that value ever changes, this comment and the two constants below need
// a matching update.
export const PLATFORM_HEADER = 1100

// Docked rail chrome — ProjectCanvasSidebar (left), CanvasViewSidebar /
// VersionHistoryPanel / VersionDetailPanel (right). Above the screen-space
// overlay band: the overlay's item TITLES painted straight across the open
// rails at the old 900 (< CANVAS_OVERLAY 950) — a docked opaque panel must
// occlude board ANNOTATIONS, not the other way round (2026-08-07 per Nika
// "fix why project canvas pages titles are above workspace ui"). Selection
// marquee/handles passing under an open rail is the correct trade — the
// rail is opaque glass, exactly like a design-tool panel over its canvas.
// Still below the platform header (1100), which stays supreme.
export const CANVAS_RAIL = 1000

// CanvasEditor — the fixed, pointerEvents:none screen-space overlay host
// (CanvasOverlayV2's rulers/marquee/selection/handles/item-titles/modes-bar,
// QuickEditor, CanvasV2CodeEditor). Canvas's topmost owned layer — stays
// below PLATFORM_HEADER on purpose.
export const CANVAS_OVERLAY = 950

// Live v2 viewport mirror — written by state.js applyViewport, read by
// the panzoom shim below so the outer chrome's animation-frame loop
// always sees fresh values without waiting for component init.
if (typeof topWindow !== 'undefined' && !topWindow.__cvViewport) {
  topWindow.__cvViewport = { x: 40, y: 40, scale: 0.35 }
}

if (!topWindow.__canvasGlobalScope) {
  topWindow.__canvasGlobalScope = {
    // Panzoom (v1)
    panningMode: false,
    isPanning: false,
    isGrabbing: false,
    positions: { x: 0, y: 0, scale: 0.35 },
    panzoomInstance: null,

    // Selection state (element refs — NOT stored in reactive store)
    selected: null,
    selectedKey: null,
    hovered: null,
    hoveredKey: null,

    canvasElement: null,

    // Modes (v1)
    defaultMode: true,
    inspectMode: false,
    editorActive: true,

    // Inspect layers
    hoverLayer: null,
    activeLayer: null,
    layerHostComponent: null,
    inContext: null,

    // Canvas v2 feature flag — v2 state itself lives on reactive root
    // state (see functions/canvas/state.js). This scope only carries the
    // flag because it's read by both editor chrome and iframe.
    // Canvas v2 is now the default. Legacy gridstack canvas (v1) is
    // opt-in via `?canvasOld=true` or `?canvasV2=0`; also kept
    // overridable via localStorage `canvasOld` for stable bookmarks.
    canvasV2: (() => {
      try {
        if (typeof window === 'undefined') return true
        const sp = new URLSearchParams(window.location.search)
        if (sp.get('canvasV2') === '1') return true
        if (sp.get('canvasV2') === '0') return false
        if (sp.get('canvasOld') === 'true' || sp.get('canvasOld') === '1') return false
        if (window.localStorage?.getItem('canvasOld')) return false
        return true
      } catch {
        return true
      }
    })()
  }
}

// Install the v1-shaped panzoom shim eagerly when v2 is on so the
// shared Zoombar's per-frame loop always has something to read. The
// shim reads `topWindow.__cvViewport`, which `applyViewport` keeps
// fresh.
if (
  topWindow.__canvasGlobalScope.canvasV2 &&
  !topWindow.__canvasGlobalScope.panzoomInstance?.__v2
) {
  // Resolves the window itself rather than closing over the module-scope
  // `topWindow` const. frank lifts this named local into `globalScope.vp` as a
  // standalone function; `topWindow` is a module binding whose value is a
  // runtime call (`resolveTopWindow()`), so it is not serializable and does not
  // travel with the lift — the emitted `vp` read a free `topWindow` and threw
  // ReferenceError on the Zoombar's very first frame at a frank-served runtime.
  // `resolveTopWindow` is an exported FUNCTION, so it lifts cleanly.
  const vp = () => resolveTopWindow().__cvViewport || { x: 40, y: 40, scale: 0.35 }
  // Write path (ZOOMBAR-PRESET-DROPDOWN-DEAD-1): the mutators route through
  // the v2 camera bridge (functions/canvas/state.js installV2CameraBridge,
  // bound to the live CanvasSurface) whenever one is installed — that is the
  // real applyViewport pipeline, so the board actually moves. Writing only
  // `__cvViewport` (the pre-bridge behaviour, kept as the fallback for the
  // window before any surface has mounted) changed the Zoombar label and
  // nothing else. Same lift-safety as `vp`: resolved per call, not closed
  // over. The fallback WRITES resolve the window per call too: frank (since
  // 3.14.799) keeps this module's default export when it rebuilds
  // globalScope, so the shim's methods are emitted standalone and a write to
  // the module `topWindow` was a free name there (PUBLISH BLOCKED, 5
  // unresolvable-identifier findings on moveTo/moveBy/zoomAbs/smoothZoom/
  // smoothZoomAbs).
  const cam = () => resolveTopWindow().__cvCamera
  topWindow.__canvasGlobalScope.panzoomInstance = {
    __v2: true,
    getTransform: () => ({ ...vp() }),
    moveTo: (x, y) => {
      if (cam()) return cam().moveTo(x, y)
      resolveTopWindow().__cvViewport = { ...vp(), x, y }
    },
    moveBy: (dx, dy) => {
      if (cam()) return cam().moveBy(dx, dy)
      const v = vp()
      resolveTopWindow().__cvViewport = { ...v, x: v.x + dx, y: v.y + dy }
    },
    zoomAbs: (_x, _y, scale) => {
      if (cam()) return cam().zoomTo(scale)
      resolveTopWindow().__cvViewport = { ...vp(), scale }
    },
    smoothZoom: (cx, cy, factor) => {
      if (cam()) return cam().zoomBy(factor)
      const v = vp()
      const ns = Math.max(0.035, Math.min(2.5, v.scale * factor))
      resolveTopWindow().__cvViewport = {
        x: cx - (cx - v.x) * (ns / v.scale),
        y: cy - (cy - v.y) * (ns / v.scale),
        scale: ns
      }
    },
    smoothZoomAbs: (_x, _y, scale) => {
      if (cam()) return cam().zoomTo(scale)
      resolveTopWindow().__cvViewport = { ...vp(), scale }
    },
    on: () => {},
    off: () => {},
    dispose: () => {}
  }
}

export default topWindow.__canvasGlobalScope
